import { useMemo } from "react";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import { PreviewLedgerDataset } from "./previewTypes";
import { filterLedgerRows } from "./filterPreviewRows";
import { previewRowPercent } from "./previewPercent";
import { AmountCell } from "./AmountCell";
import { PercentCell } from "./PercentCell";
import styles from "./ReportPreview.module.scss";

interface Props {
    dataset: PreviewLedgerDataset;
    numberFormat: NumberFormatValues;
    rowVisibility: ReportSettingRowVisibility;
}

export const LedgerTable = ({
    dataset,
    numberFormat,
    rowVisibility,
}: Props) => {
    const rows = useMemo(
        () => filterLedgerRows(dataset.rows, rowVisibility),
        [dataset.rows, rowVisibility],
    );

    return (
        <div className={styles.table}>
            <div className={styles.columnHeader} data-columns="ledger">
                <span>{dataset.dateHeader}</span>
                <span>{dataset.descriptionHeader}</span>
                <span className={styles.amountCell}>
                    {formatMoneyColumnHeader(dataset.debitHeader, numberFormat)}
                </span>
                <span className={styles.amountCell}>
                    {formatMoneyColumnHeader(
                        dataset.creditHeader,
                        numberFormat,
                    )}
                </span>
                <span className={styles.amountCell}>
                    {formatMoneyColumnHeader(
                        dataset.balanceHeader,
                        numberFormat,
                    )}
                </span>
                <span className={styles.percentCell}>
                    {dataset.percentHeader}
                </span>
            </div>
            {rows.map((row) => {
                if (row.kind === "spacer") {
                    return <div key={row.id} className={styles.spacerRow} />;
                }
                if (row.kind === "account") {
                    return (
                        <div key={row.id} className={styles.ledgerAccountRow}>
                            {row.label}
                        </div>
                    );
                }
                const percent = previewRowPercent({
                    amount: row.debit ?? row.credit ?? row.balance,
                    percentBase: dataset.percentBase,
                });
                return (
                    <div
                        key={row.id}
                        className={styles.gridRow}
                        data-columns="ledger"
                        data-kind={row.kind}
                        data-top-border={row.hasTopBorder || undefined}
                    >
                        <span>{row.date}</span>
                        <span>{row.label}</span>
                        <AmountCell
                            value={row.debit}
                            numberFormat={numberFormat}
                        />
                        <AmountCell
                            value={row.credit}
                            numberFormat={numberFormat}
                        />
                        <AmountCell
                            value={row.balance}
                            numberFormat={numberFormat}
                        />
                        <PercentCell
                            value={percent}
                            numberFormat={numberFormat}
                        />
                    </div>
                );
            })}
        </div>
    );
};
